import type { ReactElement } from "react";

/** How much of an untrusted `kind` may reach the console or the DOM. */
export const MAX_DIAGNOSTIC_KIND_LENGTH = 60;

/**
 * Characters stripped from an untrusted `kind` before it is shown or logged —
 * the same categories `sanitizeKindForLog` strips on the backend, for the same
 * reasons: `Cc` (newline forges a second console entry), `Cf` (bidi overrides
 * reorder how the REST of the line renders without changing its bytes), and
 * `Zl`/`Zp` (line/paragraph separators many consoles break on).
 *
 * No `g` flag, so `.test()` keeps no `lastIndex` state and the shared instance
 * is safe to reuse.
 */
const UNSAFE_DIAGNOSTIC_CHAR = /[\p{Cc}\p{Cf}\p{Zl}\p{Zp}]/u;

/**
 * Bound AND sanitise an untrusted `kind` for display in the console or the DOM.
 *
 * Deliberately not `[...value].slice(0, max)`: the spread expands the WHOLE
 * string into an array first, so a multi-megabyte `kind` written into the Y.Doc
 * by a non-UI client would allocate proportionally on every render before the
 * truncation ever ran. Iterating stops at `max` regardless of input size, and
 * iterating by code point (not by UTF-16 unit) never splits a surrogate pair.
 *
 * Truncation alone is not enough: a SHORT payload of newlines or bidi controls
 * is well under the bound and still forges or reorders what an operator reads.
 * React escapes markup, but it does not neutralise either of those.
 */
export function sanitizeKindForDisplay(value: string, max: number): string {
  let out = "";
  let count = 0;
  for (const codePoint of value) {
    if (count >= max) break;
    out += UNSAFE_DIAGNOSTIC_CHAR.test(codePoint) ? " " : codePoint;
    count += 1;
  }
  return out;
}

/**
 * Fallback card for a `kind` that has no registered def (PB-146).
 *
 * Previously this returned `null`, but `WidgetLayer` still renders the widget's
 * absolutely-positioned `w × h` wrapper with `pointerEvents: auto` — so the
 * canvas got an invisible dead box instead of a visible failure. It happens when
 * a doc holds a kind removed from the registry, or one written straight into the
 * Y.Doc by a non-UI client.
 *
 * The visible copy is prose, never the raw `kind` — `paretoL1` on screen tells a
 * user nothing they can act on. The kind rides in `title` (and the `console.warn`
 * above) so support can still identify the card from a screenshot's tooltip.
 * `kind` arrives already bounded and sanitised by `sanitizeKindForDisplay`, so
 * neither the tooltip nor the console can be forged or reordered by doc content.
 *
 * Deliberately inert — no drag grip and no remove control. Both live in the
 * chrome built from `def`, which by definition does not exist here. Clearing one
 * is an owner action through the placement API, not a canvas gesture.
 */
export function UnsupportedWidget({
  kind,
}: {
  readonly kind: string;
}): ReactElement {
  return (
    <div
      data-testid="canvas-widget-unsupported"
      title={`Unsupported widget kind: ${kind}`}
      className="h-full w-full flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-pb-border bg-pb-card px-3 py-2 text-center"
    >
      <span className="text-dense-1 font-semibold text-pb-text">
        Unsupported widget
      </span>
      <span className="text-dense-1 text-pb-text-muted">
        This widget type is no longer available.
      </span>
    </div>
  );
}
